import {useEffect} from "react";
import {reaction} from "mobx";
import type {ViewChangeType} from "../../../data/models/ViewUtils";
import type {View, ISpotlightRule} from "../../../data/models/View";
import type {IFilterRow} from "../../../data/models/filter/Filter";
import {cleanSpotlightRulesForComparison, constructFilterTitle, isFilterComplete} from "./spotlightUtils";

function normalizeLabelsForRules(rules: ISpotlightRule[], appState: any): ISpotlightRule[] {
	return rules.map((rule) => {
		if (rule.ruleType === "conditional" && rule.filters) {
			// Only use complete filters when computing label to avoid false change detection
			const completeFilters = rule.filters.filter((filter: IFilterRow) => {
				if (filter.type === "separator") {
					return true;
				}
				return filter.type === "filter" && isFilterComplete(filter);
			});
			const label = constructFilterTitle(completeFilters, appState) || "Define Spotlight Rule";
			return {...rule, label};
		}
		if (rule.ruleType === "field" && rule.fieldRefID) {
			const fieldTitle = appState.actions.getFieldTitle(rule.fieldRefID);
			const baseLabel = fieldTitle ? `Color by ${fieldTitle}` : "Define Spotlight";

			if (rule.filters && rule.filters.length > 0) {
				// Only use complete filters when computing label
				const completeFilters = rule.filters.filter((filter: IFilterRow) => {
					if (filter.type === "separator") {
						return true;
					}
					return filter.type === "filter" && isFilterComplete(filter);
				});
				const filterTitle = constructFilterTitle(completeFilters, appState);
				const label = filterTitle ? `${baseLabel} where ${filterTitle}` : baseLabel;
				return {...rule, label};
			}
			return {...rule, label: baseLabel};
		}
		return rule;
	});
}

function normalizeSpotlightRules(spotlightRules: any, appState: any) {
	const emptyRules: any = {
		xyiconSpotlightRules: [],
		boundarySpotlightRules: [],
		listViewSpotlightRules: [],
	};

	if (!spotlightRules) {
		return emptyRules;
	}

	return {
		xyiconSpotlightRules: cleanSpotlightRulesForComparison(normalizeLabelsForRules(spotlightRules.xyiconSpotlightRules || [], appState)),
		boundarySpotlightRules: cleanSpotlightRulesForComparison(normalizeLabelsForRules(spotlightRules.boundarySpotlightRules || [], appState)),
		listViewSpotlightRules: cleanSpotlightRulesForComparison(normalizeLabelsForRules(spotlightRules.listViewSpotlightRules || [], appState)),
	};
}

const isEffectivelyEmpty = (rules: any): boolean => {
	return (
		rules && rules.xyiconSpotlightRules?.length === 0 && rules.boundarySpotlightRules?.length === 0 && rules.listViewSpotlightRules?.length === 0
	);
};

export function useUnsavedSpotlight(selectedView: View, onChangesDetected: (changes: ViewChangeType[]) => void) {
	useEffect(() => {
		if (!selectedView) {
			onChangesDetected([]);
			return;
		}

		const appState = selectedView.appState;

		const dispose = reaction(
			() => {
				const savedAdditionalDetails = JSON.parse(selectedView.data.additionalDetails || "{}");
				const normalizedSaved = normalizeSpotlightRules(savedAdditionalDetails.spotlightRules, appState);
				const normalizedCurrent = normalizeSpotlightRules(selectedView.spotlightRules, appState);

				if (isEffectivelyEmpty(normalizedSaved) && isEffectivelyEmpty(normalizedCurrent)) {
					return false;
				}

				return JSON.stringify(normalizedSaved) !== JSON.stringify(normalizedCurrent);
			},
			(hasUnsavedChanges) => onChangesDetected(hasUnsavedChanges ? ["spotlight"] : []),
			{fireImmediately: true},
		);

		return () => dispose();
	}, [selectedView, onChangesDetected]);
}
